import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtMoney, fmtPct, monthLabel } from "@/lib/format";
import { PageHeader, SectionTitle, AwaitingData } from "@/components/common/Page";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader2 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const chartTip = { backgroundColor: "#111A2E", border: "1px solid #1E2D4A", borderRadius: 8, color: "#F8FAFC", fontSize: 12 };

export default function FinanceDashboard() {
  const { data: pnl, isLoading } = useQuery({ queryKey: ["pnl"], queryFn: async () => (await api.get("/finance/pnl")).data });
  const { data: trend } = useQuery({ queryKey: ["pnl-trend"], queryFn: async () => (await api.get("/finance/pnl-trend")).data });
  const { data: gst } = useQuery({ queryKey: ["gst"], queryFn: async () => (await api.get("/finance/gst")).data });

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;

  const p = pnl.pnl, rev = pnl.revenue, exp = pnl.expenses;
  const hasData = rev.total_ex > 0 || exp.total_ex > 0;
  const chartData = (trend?.series || []).map((s) => ({ ...s, label: s.month.slice(2) }));

  return (
    <div className="ns-fade">
      <PageHeader title="Finance Dashboard" subtitle={`Management accounting · ${monthLabel(pnl.month)}`} testId="finance-dashboard-header" />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testId="fin-revenue" label="Revenue (ex-GST)" value={hasData ? fmtMoney(rev.total_ex) : <AwaitingData />} sub={`GST ${fmtMoney(rev.total_gst)}`} accent="cyan" delay={0} />
        <KpiCard testId="fin-cogs" label="Cost of Revenue" value={hasData ? fmtMoney(p.cogs) : <AwaitingData />} accent="amber" delay={40} />
        <KpiCard testId="fin-gross" label="Gross Profit" value={hasData ? fmtMoney(p.gross_profit) : <AwaitingData />} sub={`Margin ${fmtPct(p.gross_margin)}`} accent="emerald" delay={80} />
        <KpiCard testId="fin-operating" label="Operating Profit" value={hasData ? fmtMoney(p.operating_profit) : <AwaitingData />} sub={`Margin ${fmtPct(p.operating_margin)}`} accent={p.operating_profit >= 0 ? "emerald" : "rose"} delay={120} />
        <KpiCard testId="fin-mrr" label="MRR" value={pnl.mrr ? fmtMoney(pnl.mrr) : <AwaitingData />} accent="cyan" delay={160} />
        <KpiCard testId="fin-arr" label="ARR" value={pnl.arr ? fmtMoney(pnl.arr) : <AwaitingData />} accent="cyan" delay={200} />
        <KpiCard testId="fin-recurring" label="Recurring Revenue" value={hasData ? fmtPct(rev.recurring_pct) : <AwaitingData />} sub="of total revenue" accent="slate" delay={240} />
        <KpiCard testId="fin-gst-net" label="Net GST Payable" value={gst ? fmtMoney(gst.net_payable) : <AwaitingData />} sub={gst?.period} accent="slate" delay={280} />
      </div>

      <SectionTitle className="mb-3 mt-8">12-Month P&L Trend</SectionTitle>
      <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
        {chartData.some((d) => d.revenue || d.opex || d.cogs) ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={chartTip} formatter={(v) => fmtMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#22D3EE" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cogs" name="COGS" fill="#A855F7" radius={[3, 3, 0, 0]} />
              <Bar dataKey="opex" name="OpEx" fill="#3B82F6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="operating_profit" name="Op. Profit" fill="#10B981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[300px] flex-col items-center justify-center text-center">
            <AwaitingData label="No financial history yet" />
            <p className="mt-2 max-w-sm text-xs text-slate-500">Add revenue and expense records — the trend will populate automatically from recorded transactions.</p>
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
          <SectionTitle className="mb-4">Cost Structure (this month)</SectionTitle>
          <Row l="Cost of Revenue (COGS)" v={exp.cogs} pct={exp.total_ex ? exp.cogs / exp.total_ex * 100 : 0} c="bg-purple-500" />
          <Row l="Operating Expenses" v={exp.opex} pct={exp.total_ex ? exp.opex / exp.total_ex * 100 : 0} c="bg-blue-500" />
          <div className="my-3 border-t border-[#1E2D4A]" />
          <Row l="Fixed Costs" v={exp.fixed} pct={exp.total_ex ? exp.fixed / exp.total_ex * 100 : 0} c="bg-indigo-500" />
          <Row l="Variable Costs" v={exp.variable} pct={exp.total_ex ? exp.variable / exp.total_ex * 100 : 0} c="bg-pink-500" />
        </div>
        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
          <SectionTitle className="mb-4">Receivables & GST</SectionTitle>
          <Row l="Paid (incl. GST)" v={rev.paid_inc} c="bg-emerald-500" />
          <Row l="Outstanding (incl. GST)" v={rev.outstanding_inc} c="bg-amber-500" />
          <div className="my-3 border-t border-[#1E2D4A]" />
          <Row l="GST Collected" v={gst?.gst_collected || 0} c="bg-cyan-500" />
          <Row l="GST Credits" v={gst?.gst_credits || 0} c="bg-slate-500" />
          {gst && <p className="mt-4 text-[11px] italic text-slate-500">{gst.disclaimer}</p>}
        </div>
      </div>
    </div>
  );
}

const Row = ({ l, v, pct, c }) => (
  <div className="mb-3">
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-300">{l}</span>
      <span className="font-mono-fin font-semibold text-slate-100">{fmtMoney(v)}</span>
    </div>
    {pct !== undefined && (
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#0B1120]">
        <div className={`h-full rounded-full ${c}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    )}
  </div>
);
